import { useCallback, useEffect, useMemo, useState } from 'react';
import { Heading } from "@/components/ui/typography";
import { Link, useNavigate } from 'react-router-dom';

import { ListSkeleton } from '@/components/skeletons/SkeletonLayouts';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { track } from '@/lib/analytics/track';
import { KycStartError, startKyc as launchKycSession } from '@/lib/kyc/start';
import { getOnboarding, type OnboardingResponse } from '@/lib/rpc/user/onboarding';
import { Page, PageToolbar, StatusChip, EmptyState } from '@supabank/ui';
import type { PageToolbarProps } from '@supabank/ui';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';

/**
 * ResumeVerificationPage
 *
 * Purpose: Personalized welcome screen for returning users who already completed profile
 *
 * Flow:
 * 1. User returns after completing OnboardingProfile
 * 2. OnboardingProfile redirects here instead of showing form again
 * 3. This page shows personalized welcome + account type
 * 4. User clicks "Continue to Verification" → launches KYC with correct account_type
 *
 * Features:
 * - Displays user's name and account type (Individual/Business)
 * - Shows current KYC status
 * - Allows editing profile if needed
 * - Auto-launches KYC if user is ready
 */
export default function ResumeVerificationPage() {
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<OnboardingResponse | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [retryAfterSeconds, setRetryAfterSeconds] = useState<number | null>(null);

  const toolbarItems = useMemo<PageToolbarProps['items']>(() => [
    { id: 'search', hotkey: 'K', ariaLabel: 'Search onboarding help', placeholder: 'Search verification help' },
    { id: 'refresh', hotkey: 'R', ariaLabel: 'Refresh verification status' },
    { id: 'filters', hotkey: 'F', ariaLabel: 'Edit profile information' },
    { id: 'export', hotkey: 'E', ariaLabel: 'Download verification summary' },
    { id: 'primary', hotkey: 'N', ariaLabel: 'Continue verification', label: 'Continue Verification' },
  ], []);
  const navigate = useNavigate();

  // Fetch user's onboarding status
  const refreshStatus = useCallback(async () => {
    setStatusLoading(true);
    setError(null);

    try {
      const response = await getOnboarding();
      setStatus(response);

      // If user hasn't completed profile, redirect to profile form
      if (response.state === 'needs_profile') {
        console.log('[resume] User needs profile, redirecting to /onboarding/profile');
        track('resume_redirect_needs_profile');
        navigate('/onboarding/profile', { replace: true });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to get your verification status.';
      setError(message);
      track('resume_status_error', { error: message });
    } finally {
      setStatusLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    void refreshStatus();
  }, [refreshStatus]);

  // Countdown timer for rate limiting
  useEffect(() => {
    if (retryAfterSeconds === null || retryAfterSeconds <= 0) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setRetryAfterSeconds(prev => {
        if (prev == null) return null;
        const next = Math.max(prev - 1, 0);
        if (next === 0) {
          setError(null);
        }
        return next;
      });
    }, 1000);

    return () => window.clearTimeout(timeoutId);
  }, [retryAfterSeconds]);

  const launchKyc = useCallback(
    async (forceNew: boolean) => {
      if (launching || (retryAfterSeconds && retryAfterSeconds > 0)) {
        return;
      }

      setLaunching(true);
      setError(null);
      setRetryAfterSeconds(null);

      try {
        // Launch KYC with account_type from status
        const result = await launchKycSession({
          force_new: forceNew,
        });

        const redirectUrl = result.url ?? result.kyc_link;

        if (!redirectUrl) {
          setError('Failed to get verification link. Please try again.');
          track('resume_kyc_missing_url', {
            hasUrl: Boolean(result.url),
            hasKycLink: Boolean(result.kyc_link),
          });
          return;
        }

        track('resume_kyc_launched', {
          forceNew,
          accountType: status?.account_type ?? 'unknown',
          endorsement: result.endorsement,
        });

        // Redirect to KYC
        if (typeof window !== 'undefined') {
          window.location.assign(redirectUrl);
        }
      } catch (err) {
        if (err instanceof KycStartError && err.status === 429) {
          const retryAfter = err.retryAfterSeconds && err.retryAfterSeconds > 0 ? err.retryAfterSeconds : 30;
          setRetryAfterSeconds(retryAfter);
          track('resume_kyc_rate_limited', { retryAfterSeconds: retryAfter });
          setError(`Provider is busy. Retry in ${retryAfter} sec.`);
          return;
        }

        const message = err instanceof Error ? err.message : 'Failed to open verification.';
        setError(message);
        track('resume_kyc_error', { error: message });
      } finally {
        setLaunching(false);
      }
    },
    [launching, retryAfterSeconds, status?.account_type]
  );

  // Loading state
  // Get display name and account type badge
  const displayName = status?.full_name || status?.email || 'there';
  const accountType = status?.account_type;
  const accountTypeBadge = accountType === 'business' ? 'Business' : accountType === 'individual' ? 'Individual' : null;

  // Render different states based on KYC status
  const renderContent = () => {
    switch (status?.state) {
      case 'approved':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Heading level={1}>Verification Complete!</Heading>
              <p className="text-sm text-muted-foreground">
                Your identity verification has been approved. You can now access all features of Supabank.
              </p>
            </div>
            <Button asChild>
              <Link to="/dashboard">Go to Dashboard</Link>
            </Button>
          </div>
        );

      case 'pending_review':
      case 'under_review':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Heading level={1}>Welcome back, {displayName}!</Heading>
              {accountTypeBadge && (
                <Badge variant="secondary" className="text-sm">
                  {accountTypeBadge} Account
                </Badge>
              )}
              <p className="text-sm text-muted-foreground">
                Your documents are currently under review. We'll notify you by email once the verification is complete.
              </p>
            </div>
            <div className="flex gap-3">
              <Button onClick={() => void refreshStatus()} disabled={statusLoading}>
                Check Status
              </Button>
              <Button variant="outline" asChild>
                <Link to="/dashboard">Return to Dashboard</Link>
              </Button>
            </div>
          </div>
        );

      case 'blocked':
      case 'rejected':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Heading level={1}>Verification Issue</Heading>
              <p className="text-sm text-muted-foreground">
                {status?.reason
                  ? `Reason: ${status.reason}`
                  : 'There was an issue with your verification. Please contact support for assistance.'}
              </p>
            </div>
            <Button variant="outline" asChild>
              <Link to="/dashboard">Return to Dashboard</Link>
            </Button>
          </div>
        );

      default:
        // start_kyc, resume_kyc, awaiting_questionnaire, awaiting_ubo, incomplete, link_created
        return (
          <div className="space-y-4">
            <div className="space-y-3">
              <Heading level={1}>Welcome back, {displayName}!</Heading>
              {accountTypeBadge && (
                <Badge variant="secondary" className="text-sm">
                  {accountTypeBadge} Account
                </Badge>
              )}
              <p className="text-sm text-muted-foreground">
                Your profile is ready. Click below to continue your identity verification process.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <Button onClick={() => launchKyc(false)} disabled={launching || (retryAfterSeconds ?? 0) > 0}>
                {launching ? 'Opening...' : 'Continue to Verification'}
              </Button>
              <Button variant="outline" asChild>
                <Link to="/onboarding/profile">Edit Profile</Link>
              </Button>
            </div>

            {retryAfterSeconds && retryAfterSeconds > 0 && (
              <p className="text-sm text-muted-foreground">You can retry in {retryAfterSeconds} sec.</p>
            )}

            {error && (
              <div className="rounded-md border border-destructive/20 bg-destructive/5 p-4 text-sm text-destructive" role="alert">
                {error}
              </div>
            )}
          </div>
        );
    }
  };

  const renderMainContent = () => (
    <Card>
      <CardContent className="space-y-4">{renderContent()}</CardContent>
    </Card>
  );

  const headerStatus = status?.state
    ? status.state.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
    : 'Loading';

  const handleToolbarAction: PageToolbarProps['onAction'] = (itemId) => {
    switch (itemId) {
      case 'refresh':
        void refreshStatus();
        break;
      case 'filters':
        navigate('/onboarding/profile');
        break;
      case 'export':
        window.print();
        break;
      case 'primary':
        void launchKyc(false);
        break;
      default:
        break;
    }
  };

  let body: React.ReactNode;

  if (statusLoading && !status) {
    body = (
      <Card>
        <CardContent className="py-12">
          <div role="status" aria-live="polite" className="space-y-4">
            <ListSkeleton items={4} />
            <p className="text-sm text-muted-foreground">Loading verification status...</p>
          </div>
        </CardContent>
      </Card>
    );
  } else if (error && !status) {
    body = (
      <EmptyState
        icon={() => <SFIcon icon={sfIcons.security} />}
        title="Failed to Load Status"
        description={error}
        action={{
          label: 'Retry',
          onClick: () => void refreshStatus(),
        }}
        secondaryAction={{
          label: 'Return to Dashboard',
          onClick: () => navigate('/dashboard'),
        }}
      />
    );
  } else {
    body = renderMainContent();
  }

  return (
    <Page data-ux-contract="canon">
      <PageHeader
        icon={() => <SFIcon icon={sfIcons.security} />}
        title="Resume Verification"
        subtitle="Review your onboarding progress and continue identity verification"
        badge="KYC"
      />
      <PageToolbar items={toolbarItems} onAction={handleToolbarAction} />
      <PageContent className="space-y-6">
        <StatusChip intent="info">Current status: {headerStatus}</StatusChip>
        <div className="mx-auto max-w-2xl space-y-6">{body}</div>
      </PageContent>
    </Page>
  );
}
